/**
 * Bedrock WebSocket Provider
 *
 * Manages the WebSocket connection to the AI agent for real-time message
 * streaming (AG-UI v1 events). Conversation CRUD (list, load, rename, delete)
 * is handled by the Chat REST API (see services/chatApi.ts), not this provider.
 */

import React, {createContext, useContext, useEffect, useRef, useState, useCallback} from 'react'
import {getLogger} from '@/shared/utils'
import {useSubscription} from '@/shared/utils'
import {Auth} from 'aws-amplify'
import type {AgentAnswerFormat} from './formatAgentXmlToMarkdown'
import {
  setConversationTitleInList,
  touchConversationInList,
  type ConversationListItem,
} from './conversationListHelpers'
import type {ContextUsage} from './omicsContextUsage'
import type {AgentWireEvent} from './agentWireEvent'
import {parseBedrockWireEvents} from './parseBedrockWireEvents'
import {buildCancelStreamPayload} from './cancelStreamPayload'
import type {FileChange} from './filesChanged'

export type {ContextUsage} from './omicsContextUsage'

/** Chat model option from server (id = Bedrock model ID, label for display). */
export interface ChatModelOption {
  id: string
  label: string
}

/** Token usage data returned by the agent for a single response. */
export interface TokenUsage {
  input_tokens: number
  output_tokens: number
  total_tokens: number
  llm_calls: number
}

/** Server-side cloud unit estimate (computed by Lambda from token usage + model pricing). */
export interface CloudUnits {
  units: number
  modelLabel: string
}

interface BedrockMessage {
  type: string
  response?: string
  messageId?: string
  sessionId?: string
  connectionId?: string
  error?: string
  timestamp: string
  models?: ChatModelOption[]
  conversationId?: string
  title?: string
  conversations?: { id: string; title: string; sessionId?: string; createdAt: string; updatedAt: string; hasUpdates?: boolean }[]
  messages?: { role: string; content: string; timestamp: string }[]
  /** Tools used for this response (e.g. search_internet) - shown as label in UI */
  toolsUsed?: string[]
  /** KB document sources when query_omicsbox_kb was used - for citations */
  kbSources?: KbSource[]
  /** Web sources when search_internet was used - for citations with links */
  internetSources?: InternetSource[]
  /** Token usage for this response (input/output/total tokens + LLM calls) */
  tokenUsage?: TokenUsage
  /** Model identifier used for this response (e.g. "bedrock/anthropic.claude-sonnet-4-5-...") */
  modelUsed?: string
  /** Server-computed cloud unit estimate */
  cloudUnits?: CloudUnits
  /** AgentCore display id from AWS, e.g. omicsai_bioagent_v3-24 */
  agentRuntimeLabel?: string
}

export interface UserS3Context {
  bucket: string
  prefix: string
}

/**
 * Reference from a chat chip (`attached_file_refs`). A file/folder (dragged from cloud files or a
 * Results tab) has a `cloud:` path, folders ending with `/`; the agent derives the identity from
 * the token. A job (dragged from the Jobs panel) has its WES `run_id` instead.
 */
export type AttachedFileRef =
  | {name: string; path: string; type: 'file' | 'folder'}
  | {name: string; type: 'job'; run_id: string; tool?: string}

/** Chat mode: simple (answer + files + internet), research (full tool harness). */
export type ChatMode = 'simple' | 'research'

export interface SendMessageOptions {
  /** File refs from chat file tags (`attached_file_refs`), addressed by `cloud:` path. */
  attachedFileRefs?: AttachedFileRef[]
  /** Chat mode. Defaults to "research" if omitted. */
  mode?: ChatMode
  /** Model picked in the sending conversation's composer; falls back to the default model. */
  chatModelId?: string
  /** When true: skip relevance check and return verbose step log in the chat response (for debugging). */
  debugMode?: boolean
  /** When true: the previous response was cancelled/stopped by the user before it completed. */
  previousCancelled?: boolean
}

interface BedrockSocketContextType {
  socket: WebSocket | null
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error'
  /** Send user text for a specific conversation session. */
  sendForSession: (
    conversationId: string,
    agentSessionId: string,
    message: string,
    messageId: string,
    options?: SendMessageOptions,
  ) => void
  /** Cancel an in-flight run for a specific conversation session. */
  cancelForSession: (
    messageId: string,
    conversationId: string,
    agentSessionId: string,
  ) => void
  resetSession: () => void
  /** WebSocket connection id from whoAmI (not LangGraph thread). */
  connectionId: string | null
  setUserS3: (ctx: UserS3Context | null) => void
  availableModels: ChatModelOption[]
  selectedModelId: string | null
  setSelectedModelId: (id: string | null) => void
  /** List of conversations. Managed by Chat API in layout; `hasUpdates` flags unseen turns. */
  conversationsList: ConversationListItem[]
  setConversationsList: React.Dispatch<React.SetStateAction<ConversationListItem[]>>
  /** Touch sidebar list metadata when a session sends or receives updates. */
  touchConversationListEntry: (id: string, placeholderTitle?: string) => void
  /** A user renamed this conversation: a generated title arriving later must not replace it. */
  noteConversationRenamed: (id: string) => void
}

const BedrockSocketContext = createContext<BedrockSocketContextType | undefined>(undefined)

/** KB source reference for citations when query_omicsbox_kb was used. */
export interface KbSource {
  source: string
  relevance?: number
}

/** Web source reference for citations when search_internet was used. */
export interface InternetSource {
  title?: string
  url: string
}

/** Metadata passed when a response is complete (e.g. tools used for transparency). */
export interface ChunkCompleteMetadata {
  toolsUsed?: string[]
  /** Suggested follow-up prompts for this turn - rendered as clickable chips. */
  suggestedFollowups?: string[]
  /** Cloud runs submitted this turn that are not yet complete - for async completion tracking. */
  pendingRuns?: Array<{run_id: string; tool?: string; state?: string}>
  /** Run ids this turn reports as finished - clears their pending tag without a notification. */
  resolvedRuns?: string[]
  tokenUsage?: TokenUsage
  modelUsed?: string
  /** Document sources when KB was queried - for citations in UI */
  kbSources?: KbSource[]
  /** Web sources when search_internet was used - for citations with links in UI */
  internetSources?: InternetSource[]
  /** Server-computed cloud unit estimate */
  cloudUnits?: CloudUnits
  /** AgentCore display id from AWS, e.g. omicsai_bioagent_v3-24 */
  agentRuntimeLabel?: string
  /** Canonical user-visible answer (RUN_FINISHED.answer). */
  answer?: string
  /** When ``markdown_v1``, agent stripped legacy XML server-side; UI may skip TS regex pass on final text. */
  answerFormat?: AgentAnswerFormat
  /** Structured timeline for replay (schema_version + entries). */
  timeline?: Record<string, unknown>
  toolCallCounts?: Record<string, number>
  /** Partial streaming: which narrative bucket this token belongs to. */
  segmentKind?: 'preamble' | 'reasoning' | 'final'
  /** Model generation step (stable across token deltas for one completion). */
  stepIndex?: number
  /** Inline alert (cancel, tool failure, context summarized). */
  streamAlert?: { kind: 'cancel' | 'error' | 'info'; message: string }
  /** Context window fill for the composer ring. */
  contextUsage?: ContextUsage
  /** True when Deep Agents compacted the thread this turn. */
  contextCompacted?: boolean
  /** True when RUN_INTERRUPTED (user stopped mid-run). */
  interrupted?: boolean
  /** Files this turn created, changed, moved or deleted (`files_changed`); omitted when none. */
  filesChanged?: FileChange[]
  /** Full number of changes when `files_changed` was shortened (`files_changed_total`). */
  filesChangedTotal?: number
  /** `files_changed` is shorter than what the turn changed (`files_changed_truncated`). */
  filesChangedTruncated?: true
}

/** Payload for agent step (all intermediate steps, plus debug-specific fields when debug_mode). */
export interface ExecuteDebug {
  executor: string
  code_length: number
  result_length: number
  has_error: boolean
}

export interface StepPayload {
  type: 'step' | 'tool_call_start' | 'tool_call_end'
  stepIndex: number
  node?: string
  /** Raw content of this step (always present). */
  content?: string
  segmentKind?: 'preamble' | 'reasoning' | 'final'
  /** Debug only: prompt sent to LLM. */
  promptSent?: string
  /** Debug only: model output. */
  assistantContent?: string
  /** Debug only: execution metadata (executor type, code length, etc.) */
  debug?: ExecuteDebug
  /** Debug only: observation text. */
  observation?: string
  toolCallId?: string
  toolName?: string
  toolInput?: Record<string, unknown>
  displaySummary?: string
  toolResult?: string
  toolOk?: boolean
  toolError?: string
  messageId?: string
  sessionId?: string
}

interface BedrockSocketProviderProps {
  children: React.ReactNode
  onMessage?: (message: BedrockMessage) => void
  /** Demux target: one event per conversation session store. */
  onWireEvent?: (event: AgentWireEvent) => void
  /** Global transport errors (e.g. WebSocket disconnect) when no session routing applies. */
  onTransportError?: (error: string) => void
  autoReconnect?: boolean
  /**
   * When `.current` is set, invoked with each WebSocket text frame exactly as received (before JSON.parse).
   * Use a ref so the callback can change without reconnecting the socket.
   */
  rawWebSocketFrameSinkRef?: React.MutableRefObject<((frameText: string) => void) | null>
}

export const BedrockSocketProvider: React.FC<BedrockSocketProviderProps> = ({
  children,
  onMessage,
  onWireEvent,
  onTransportError,
  autoReconnect = true,
  rawWebSocketFrameSinkRef,
}) => {
  const [socket, setSocket] = useState<WebSocket | null>(null)
  const [connectionStatus, setConnectionStatus] = useState<
    'connecting' | 'connected' | 'disconnected' | 'error'
  >('disconnected')
  const [connectionId, setConnectionId] = useState<string | null>(null)
  const [availableModels, setAvailableModels] = useState<ChatModelOption[]>([])
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null)
  const userS3Ref = useRef<UserS3Context | null>(null)
  const [conversationsList, setConversationsList] = useState<{ id: string; title: string; createdAt: string; updatedAt: string }[]>([])
  const onWireEventRef = useRef(onWireEvent)
  const onTransportErrorRef = useRef(onTransportError)
  onWireEventRef.current = onWireEvent
  onTransportErrorRef.current = onTransportError

  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const maxReconnectAttempts = 5
  const logger = getLogger()
  const {subscriptionId, applyBalance} = useSubscription()
  // Ref so a new callback identity (subscription switch) does not reconnect the socket.
  const applyBalanceRef = useRef(applyBalance)
  applyBalanceRef.current = applyBalance

  const renamedConversationIdsRef = useRef(new Set<string>())
  const noteConversationRenamed = useCallback((id: string) => {
    renamedConversationIdsRef.current.add(id)
  }, [])

  /** A turn happened: move the conversation to the top. ``placeholderTitle`` only names a new/unnamed one. */
  const touchConversationListEntry = useCallback(
    (id: string, placeholderTitle?: string) => {
      setConversationsList((prev) => touchConversationInList(prev, id, placeholderTitle ? {placeholderTitle} : undefined))
    },
    [],
  )

  const wsUrl = process.env.REACT_APP_BEDROCK_WS_URL || 'wss://ws.omicsai.biobam.com'

  /** Full AgentCore runtime ARN; sent as `bedrock_runtime_arn` on each message when set (must match Lambda allowlist). */
  const bedrockRuntimeArn = process.env.REACT_APP_BEDROCK_AGENT_RUNTIME_ARN?.trim() || undefined

  const connectSocket = useCallback(() => {
    if (socket?.readyState === WebSocket.OPEN || connectionStatus === 'connecting') {
      return // Already connected or connecting
    }

    const doConnect = async () => {
      setConnectionStatus('connecting')
      logger.info('🔗 Connecting to WebSocket')
      try {
        // Fetch fresh Cognito ID token and attach as query param for authorizer
        const session = await Auth.currentSession()
        const idToken = session.getIdToken().getJwtToken()
        const url = new URL(wsUrl)
        url.searchParams.set('auth', idToken)

        const newSocket = new WebSocket(url.toString())

        newSocket.onopen = () => {
          logger.info('✅ WebSocket connected')
          setConnectionStatus('connected')
          reconnectAttemptsRef.current = 0
          setSocket(newSocket)

          newSocket.send(JSON.stringify({
            action: 'whoAmI',
            timestamp: new Date().toISOString(),
          }))
          newSocket.send(JSON.stringify({
            action: 'getAvailableModels',
            timestamp: new Date().toISOString(),
          }))
          // Note: conversations are loaded via the Chat API, not WebSocket.
        }

        newSocket.onmessage = async (event) => {
        try {
          const raw = typeof event.data === 'string' ? event.data : await (event.data as Blob).text()
          rawWebSocketFrameSinkRef?.current?.(raw)
          const message: BedrockMessage = JSON.parse(raw)
          logger.debug('📨 WS message', message.type)

          const wireEvents = parseBedrockWireEvents(message as unknown as Parameters<typeof parseBedrockWireEvents>[0])
          for (const wireEvent of wireEvents) {
            if (wireEvent.type === 'balance') {
              // App-wide state (header, dashboard, composer lock) — not routed to a session.
              applyBalanceRef.current(wireEvent.balanceRemaining)
              continue
            }
            if (
              (wireEvent.type === 'chunk' && wireEvent.isComplete) ||
              wireEvent.type === 'run_started'
            ) {
              const convId = wireEvent.conversationId
              if (convId) {
                // RUN_STARTED is sent after the server created the row, so it can be renamed now.
                setConversationsList((prev) => touchConversationInList(prev, convId, {saved: true}))
              }
            }
            // A rename on this client wins over a generated title still in flight (the server
            // already refuses to store it; this keeps the open tab and history from flipping back).
            if (
              wireEvent.type === 'conversation_title' &&
              wireEvent.conversationId &&
              !renamedConversationIdsRef.current.has(wireEvent.conversationId)
            ) {
              const {conversationId: titledId, title} = wireEvent
              setConversationsList((prev) => setConversationTitleInList(prev, titledId, title))
            }
            onWireEventRef.current?.(wireEvent)
          }

          switch (message.type) {
            case 'whoAmI':
            case 'connection_established':
              logger.debug('🆔 Connection established', { session: message.sessionId || message.connectionId })
              if (message.sessionId || message.connectionId) {
                setConnectionId(message.sessionId ?? message.connectionId ?? null)
              }
              break

            case 'availableModels':
              if (message.models?.length) {
                setAvailableModels(message.models)
                setSelectedModelId((prev) => (prev ? prev : message.models![0].id))
              }
              break

            default:
              break
          }

          // Pass all messages to the general onMessage handler
          if (onMessage) {
            onMessage(message)
          }
        } catch (error) {
          logger.error('❌ Error parsing WebSocket message:', error)
        }
        }

        newSocket.onclose = (event) => {
        logger.warn('🔌 Bedrock WebSocket closed:', event.code, event.reason)
        setSocket(null)
        setConnectionStatus('disconnected')

        // Tell the sessions, or a turn that was in flight stays "running" forever: the composer
        // keeps showing Stop instead of Send and the chat looks frozen with no way back except a
        // reload. Reported in production as "the website isn't responsive".
        if (onTransportErrorRef.current) {
          onTransportErrorRef.current('WebSocket connection closed')
        }

        // Auto-reconnect logic
        if (autoReconnect && reconnectAttemptsRef.current < maxReconnectAttempts) {
          // Exponential backoff with jitter
          const baseDelay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000)
          const jitter = Math.floor(baseDelay * (0.2 * Math.random()))
          const delay = baseDelay + jitter
          logger.warn(`🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1})`)

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++
            connectSocket()
          }, delay)
        }
        }

        newSocket.onerror = (error) => {
        logger.error('💥 Bedrock WebSocket error', error)
        setConnectionStatus('error')
        if (onTransportErrorRef.current) {
          onTransportErrorRef.current('WebSocket connection error')
        }
        }
      } catch (error) {
        logger.error('❌ Failed to create WebSocket', error)
        setConnectionStatus('error')
        if (onTransportErrorRef.current) {
          onTransportErrorRef.current('WebSocket connection error')
        }
      }
    }

    void doConnect()
  }, [wsUrl, autoReconnect, onMessage, logger, socket?.readyState, connectionStatus, rawWebSocketFrameSinkRef])

  const sendForSession = useCallback(
    (
      conversationId: string,
      agentSessionId: string,
      message: string,
      messageId: string,
      options?: SendMessageOptions,
    ) => {
      if (socket?.readyState === WebSocket.OPEN) {
        const payload: {
          action: string
          message: string
          sessionId: string
          timestamp: string
          mode?: ChatMode
          messageId: string
          chat_model_id?: string
          user_s3?: UserS3Context
          attached_file_refs?: AttachedFileRef[]
          debug_mode?: boolean
          conversationId: string
          subscription_id?: string
          bedrock_runtime_arn?: string
        } = {
          action: 'sendMessage',
          message,
          sessionId: agentSessionId,
          timestamp: new Date().toISOString(),
          messageId,
          conversationId,
        }

        const chatModelId = options?.chatModelId ?? selectedModelId
        if (chatModelId) payload.chat_model_id = chatModelId
        if (userS3Ref.current?.bucket && userS3Ref.current?.prefix) {
          payload.user_s3 = userS3Ref.current
        }
        if (options?.attachedFileRefs?.length) {
          payload.attached_file_refs = options.attachedFileRefs
        }
        if (options?.mode) {
          payload.mode = options.mode
        }
        if (options?.debugMode) {
          payload.debug_mode = true
        }
        if (options?.previousCancelled) {
          (payload as Record<string, unknown>).previous_cancelled = true
        }

        if (subscriptionId) {
          payload.subscription_id = subscriptionId
        }
        if (bedrockRuntimeArn) {
          payload.bedrock_runtime_arn = bedrockRuntimeArn
        }

        logger.debug('📤 Sending message', {
          sessionId: payload.sessionId,
          messageId,
          mode: payload.mode,
          conversationId: payload.conversationId,
          hasSubscriptionId: Boolean(subscriptionId),
        })
        socket.send(JSON.stringify(payload))
      } else {
        logger.warn('⚠️ WebSocket not connected, cannot send message')
        onTransportErrorRef.current?.('WebSocket not connected')
      }
    },
    [socket, logger, selectedModelId, subscriptionId, bedrockRuntimeArn]
  )

  const cancelForSession = useCallback(
    (messageId: string, conversationId: string, agentSessionId: string) => {
      if (socket?.readyState === WebSocket.OPEN) {
        logger.debug('🛑 Sending cancelStream', {messageId, sessionId: agentSessionId, conversationId})
        socket.send(JSON.stringify(buildCancelStreamPayload({
          messageId,
          sessionId: agentSessionId,
          conversationId,
          timestamp: new Date().toISOString(),
          bedrockRuntimeArn,
        })))
      } else {
        logger.warn('⚠️ WebSocket not connected, cannot cancel stream')
      }
    },
    [socket, logger, bedrockRuntimeArn]
  )

  const setUserS3 = useCallback((ctx: UserS3Context | null) => {
    userS3Ref.current = ctx
  }, [])

  const resetSession = useCallback(() => {
    if (socket?.readyState === WebSocket.OPEN) {
      logger.debug('🔄 Requesting session reset')
      socket.send(
        JSON.stringify({
          type: 'reset_session',
          timestamp: new Date().toISOString(),
        })
      )
    }
  }, [socket, logger])

  // Initialize connection on mount
  useEffect(() => {
    connectSocket()

    // Cleanup on unmount only
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (socket) {
        socket.close(1000, 'Component unmounting')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty dependency array - only run on mount/unmount

  const value: BedrockSocketContextType = {
    socket,
    connectionStatus,
    sendForSession,
    cancelForSession,
    resetSession,
    connectionId,
    setUserS3,
    availableModels,
    selectedModelId,
    setSelectedModelId,
    conversationsList,
    setConversationsList,
    touchConversationListEntry,
    noteConversationRenamed,
  }

  return <BedrockSocketContext.Provider value={value}>{children}</BedrockSocketContext.Provider>
}

export const useBedrockSocket = (): BedrockSocketContextType => {
  const context = useContext(BedrockSocketContext)
  if (context === undefined) {
    throw new Error('useBedrockSocket must be used within a BedrockSocketProvider')
  }
  return context
}